import { z } from 'zod';
import { promises as fs } from 'node:fs';
import path from 'node:path';
import { typedOk, typedErr, type ToolDefinition } from '../toolTypes.js';
import { detectNewline, fromLF, splitLinesLF } from './newlines.js';
import type { WriteReject } from './edit.js';

/**
 * diff — show_diff + apply_diff.
 *
 * Zero-deps unified diff implementation (Myers LCS-based, ~150 LOC).
 * Designed for coding-agent workflows where the model needs to:
 *   - Preview edits without applying them (show_diff, dryRun)
 *   - Apply unified-diff patches generated by itself or pasted from PRs (apply_diff)
 *
 * Format: standard unified diff with `--- old`, `+++ new`, `@@ -a,b +c,d @@` hunks.
 * ADR-0033 (zero relocation): every hunk applies at its DECLARED @@ position on
 * the original lines only. A mismatched/drifted hunk is rejected with a
 * structured WriteReject — it is never searched for elsewhere. The only
 * tolerances are LF/CRLF normalization and, with fuzzyMatch=true, whitespace —
 * both strictly AT the declared position.
 */

interface Hunk {
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  /** Operations: ' ' (context), '-' (remove), '+' (add) */
  ops: Array<{ kind: ' ' | '-' | '+'; text: string }>;
}

interface DiffHunkSummary {
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  added: number;
  removed: number;
}

// ────────────────────────────────────────────────────────────────────────────
// LCS-based diff (simplified Myers)
// ────────────────────────────────────────────────────────────────────────────

function lcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

/**
 * Cap on LCS DP cells, (m+1)×(n+1). ~4M cells ≈ 32MB of number arrays —
 * without it a 5k-line file diff allocates a ~200MB matrix.
 */
const LCS_MAX_CELLS = 4_000_000;

/**
 * Compute the full op sequence (' ' context / '-' remove / '+' add) between
 * two line arrays, or null when the changed region is too large for the
 * quadratic DP.
 *
 * Common prefix/suffix line runs are trimmed first, so a small edit inside a
 * large file reduces the DP to just the edited region (the standard
 * optimization that keeps whole-file diffs cheap).
 */
function diffOps(a: string[], b: string[]): Array<{ kind: ' ' | '-' | '+'; text: string }> | null {
  let lo = 0;
  while (lo < a.length && lo < b.length && a[lo] === b[lo]) lo++;
  let hiA = a.length;
  let hiB = b.length;
  while (hiA > lo && hiB > lo && a[hiA - 1] === b[hiB - 1]) {
    hiA--;
    hiB--;
  }
  const midA = a.slice(lo, hiA);
  const midB = b.slice(lo, hiB);
  if ((midA.length + 1) * (midB.length + 1) > LCS_MAX_CELLS) return null;

  const ops: Array<{ kind: ' ' | '-' | '+'; text: string }> = [];
  for (let k = 0; k < lo; k++) ops.push({ kind: ' ', text: a[k] });
  if (midA.length > 0 || midB.length > 0) {
    const dp = lcsTable(midA, midB);
    const segStart = ops.length;
    let i = midA.length;
    let j = midB.length;
    while (i > 0 && j > 0) {
      if (midA[i - 1] === midB[j - 1]) {
        ops.push({ kind: ' ', text: midA[i - 1] });
        i--; j--;
      } else if (dp[i - 1][j] >= dp[i][j - 1]) {
        ops.push({ kind: '-', text: midA[i - 1] });
        i--;
      } else {
        ops.push({ kind: '+', text: midB[j - 1] });
        j--;
      }
    }
    while (i > 0) { ops.push({ kind: '-', text: midA[i - 1] }); i--; }
    while (j > 0) { ops.push({ kind: '+', text: midB[j - 1] }); j--; }
    // The backtrack emitted the middle segment in reverse order.
    for (let l = ops.length - 1, r = segStart; l > r; l--, r++) {
      const tmp = ops[l]!;
      ops[l] = ops[r]!;
      ops[r] = tmp;
    }
  }
  for (let k = hiA; k < a.length; k++) ops.push({ kind: ' ', text: a[k] });
  return ops;
}

function buildHunks(a: string[], b: string[]): Hunk[] | null {
  const ops = diffOps(a, b);
  if (ops === null) return null;
  return groupIntoHunks(ops);
}

function groupIntoHunks(ops: Array<{ kind: ' ' | '-' | '+'; text: string }>): Hunk[] {
  const CONTEXT = 3;
  const hunks: Hunk[] = [];
  let i = 0;
  while (i < ops.length) {
    // Skip leading context that's not adjacent to a change
    while (i < ops.length && ops[i].kind === ' ') i++;
    if (i >= ops.length) break;
    // Found a change at index i. Walk back CONTEXT lines for leading context.
    const start = Math.max(0, i - CONTEXT);
    let oldStart = 0, newStart = 0;
    // Compute line numbers
    {
      let ol = 0, nl = 0;
      for (let k = 0; k < start; k++) {
        if (ops[k].kind !== '+') ol++;
        if (ops[k].kind !== '-') nl++;
      }
      oldStart = ol + 1; // 1-indexed
      newStart = nl + 1;
    }
    const hunkOps: typeof ops = [];
    // Leading context
    for (let k = start; k < i; k++) hunkOps.push(ops[k]);
    // Body: consume changes + trailing CONTEXT, but stop if we hit 2*CONTEXT
    // non-change lines (forces a new hunk).
    let nonChangeStreak = 0;
    while (i < ops.length) {
      const op = ops[i];
      hunkOps.push(op);
      if (op.kind === ' ') {
        nonChangeStreak++;
        if (nonChangeStreak > 2 * CONTEXT) {
          // Trim trailing CONTEXT and break
          hunkOps.splice(hunkOps.length - CONTEXT, CONTEXT);
          i++;
          break;
        }
      } else {
        nonChangeStreak = 0;
      }
      i++;
    }
    // Compute hunk line counts
    let oldLines = 0, newLines = 0;
    for (const op of hunkOps) {
      if (op.kind !== '+') oldLines++;
      if (op.kind !== '-') newLines++;
    }
    hunks.push({ oldStart, oldLines, newStart, newLines, ops: hunkOps });
  }
  return hunks;
}

function formatUnified(hunks: Hunk[], oldLabel: string, newLabel: string): string {
  const lines: string[] = [`--- ${oldLabel}`, `+++ ${newLabel}`];
  for (const h of hunks) {
    lines.push(`@@ -${h.oldStart},${h.oldLines} +${h.newStart},${h.newLines} @@`);
    for (const op of h.ops) {
      lines.push(op.kind + op.text);
    }
  }
  return lines.join('\n');
}

// ────────────────────────────────────────────────────────────────────────────
// Unified-diff parser
// ────────────────────────────────────────────────────────────────────────────

interface ParsedDiff {
  oldLabel: string;
  newLabel: string;
  hunks: Hunk[];
}

function parseUnified(raw: string): ParsedDiff {
  const lines = splitLinesLF(raw);
  if (lines.length < 2 || !lines[0].startsWith('--- ') || !lines[1].startsWith('+++ ')) {
    throw new Error('Invalid unified diff: missing --- / +++ headers');
  }
  const oldLabel = lines[0].slice(4).trim();
  const newLabel = lines[1].slice(4).trim();
  const hunks: Hunk[] = [];
  let i = 2;
  while (i < lines.length) {
    const m = lines[i].match(/^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/);
    if (!m) {
      i++;
      continue;
    }
    const oldStart = parseInt(m[1], 10);
    const oldLines = m[2] === undefined ? 1 : parseInt(m[2], 10);
    const newStart = parseInt(m[3], 10);
    const newLines = m[4] === undefined ? 1 : parseInt(m[4], 10);
    i++;
    const ops: Array<{ kind: ' ' | '-' | '+'; text: string }> = [];
    let consumedOld = 0, consumedNew = 0;
    while (i < lines.length && (consumedOld < oldLines || consumedNew < newLines)) {
      const ln = lines[i];
      if (ln.length === 0) {
        ops.push({ kind: ' ', text: '' });
        consumedOld++; consumedNew++; i++; continue;
      }
      const c = ln[0];
      const text = ln.slice(1);
      if (c === ' ') {
        ops.push({ kind: ' ', text });
        consumedOld++; consumedNew++; i++;
      } else if (c === '-') {
        ops.push({ kind: '-', text });
        consumedOld++; i++;
      } else if (c === '+') {
        ops.push({ kind: '+', text });
        consumedNew++; i++;
      } else {
        // Malformed hunk body
        break;
      }
    }
    hunks.push({ oldStart, oldLines, newStart, newLines, ops });
  }
  return { oldLabel, newLabel, hunks };
}

// ────────────────────────────────────────────────────────────────────────────
// Patch application — zero relocation (ADR-0033)
// ────────────────────────────────────────────────────────────────────────────

function normalizeWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function linesEqual(fileLine: string, opText: string, fuzzy: boolean): boolean {
  if (fileLine === opText) return true;
  if (fuzzy && normalizeWhitespace(fileLine) === normalizeWhitespace(opText)) return true;
  return false;
}

/**
 * Apply ALL hunks to the original file lines in a single pass.
 *
 * ADR-0033 (zero relocation): each hunk applies EXACTLY at its declared
 * position — `oldStart` (1-based) on the ORIGINAL file, never the
 * post-previous-hunk state and never another site found by context search.
 * Tolerances: LF/CRLF normalization (the caller splits lines LF-normalized)
 * and, when `fuzzy` is set, whitespace — both strictly AT the declared
 * position. Git convention: `@@ -N,0` (pure insert) anchors AFTER old line N.
 * Atomic: if ANY hunk fails, no commit happens.
 */
function applyAllHunks(
  originalLines: string[],
  hunks: Hunk[],
  fuzzy: boolean,
): {
  lines: string[];
  hunksApplied: number;
  hunksSkipped: number;
  lastReason?: string;
  failedHunk?: Hunk;
} {
  const out: string[] = [];
  let cursor = 0; // next original line index not yet copied into `out`
  let hunksApplied = 0;
  let hunksSkipped = 0;
  let lastReason: string | undefined;
  let failedHunk: Hunk | undefined;

  for (const hunk of hunks) {
    const start = hunk.oldLines === 0 ? hunk.oldStart : hunk.oldStart - 1;
    if (start < cursor) {
      // Overlapping/reordered hunks cannot be applied in declared order.
      lastReason = `Hunk at oldStart ${hunk.oldStart} overlaps the previous hunk (refusing to reorder)`;
      hunksSkipped++;
      failedHunk = hunk;
      break;
    }
    if (start > originalLines.length) {
      lastReason = `Hunk at oldStart ${hunk.oldStart} is beyond the end of the file (${originalLines.length} lines)`;
      hunksSkipped++;
      failedHunk = hunk;
      break;
    }
    while (cursor < start) {
      out.push(originalLines[cursor]);
      cursor++;
    }
    let hunkFileIdx = cursor;
    const hunkOut: string[] = [];
    let hunkOk = true;
    for (const op of hunk.ops) {
      if (op.kind === ' ' || op.kind === '-') {
        const fileLine = originalLines[hunkFileIdx] ?? '';
        if (linesEqual(fileLine, op.text, fuzzy)) {
          if (op.kind === ' ') hunkOut.push(fileLine);
          hunkFileIdx++;
        } else {
          lastReason =
            `Mismatch at line ${hunkFileIdx + 1} (hunk declares oldStart ${hunk.oldStart}): ` +
            `expected "${op.text.slice(0, 60)}", got "${fileLine.slice(0, 60)}"`;
          hunkOk = false;
          break;
        }
      } else {
        hunkOut.push(op.text);
      }
    }
    if (!hunkOk) {
      hunksSkipped++;
      failedHunk = hunk;
      break;
    }
    out.push(...hunkOut);
    cursor = hunkFileIdx;
    hunksApplied++;
  }
  while (cursor < originalLines.length) {
    out.push(originalLines[cursor]);
    cursor++;
  }
  return { lines: out, hunksApplied, hunksSkipped, lastReason, failedHunk };
}

// ────────────────────────────────────────────────────────────────────────────
// show_diff tool
// ────────────────────────────────────────────────────────────────────────────

const ShowDiffArgsSchema = z.object({
  path: z.string().min(1),
  proposedContent: z.string(),
  contextLines: z.number().int().nonnegative().default(3),
});

export { ShowDiffArgsSchema };

type ShowDiffArgs = z.infer<typeof ShowDiffArgsSchema>;

interface ShowDiffResult {
  path: string;
  diff: string;
  hunks: DiffHunkSummary[];
  added: number;
  removed: number;
  unchanged: boolean;
}

export const showDiffTool: ToolDefinition<ShowDiffArgs, ShowDiffResult> = {
  name: 'show_diff',
  description:
    'Show unified diff between current file content and proposed content. ' +
    'Read-only — does NOT modify the file. Use this to preview edits before applying ' +
    'with edit_file or apply_diff.',
  permissions: ['read'],
  sideEffect: 'none',
  timeoutMs: 15000,
  inputSchema: ShowDiffArgsSchema,
  execute: async (args, ctx) => {
    try {
      const absPath = path.isAbsolute(args.path) ? args.path : path.join(ctx.cwd, args.path);
      let current = '';
      try {
        current = await fs.readFile(absPath, 'utf-8');
      } catch (err) {
        if ((err as NodeJS.ErrnoException).code !== 'ENOENT') {
          return typedErr(err instanceof Error ? err.message : String(err));
        }
        // ENOENT → treat as empty file (creating new)
      }
      const a = splitLinesLF(current);
      const b = splitLinesLF(args.proposedContent);
      // Re-implement groupIntoHunks with custom CONTEXT for show_diff
      const CONTEXT = args.contextLines;
      // Guarded + prefix/suffix-trimmed LCS (see diffOps): a small edit in a
      // large file diffs only the changed region instead of allocating a
      // whole-file DP matrix.
      const rawOps = diffOps(a, b);
      if (rawOps === null) {
        return typedErr(
          `Diff too large: the changed region between current and proposed content exceeds the ` +
            `LCS cell cap (${LCS_MAX_CELLS} cells). Read the file in line ranges and diff smaller ` +
            `sections, or apply the edit with edit_file/apply_diff instead.`,
        );
      }

      // Group with custom CONTEXT
      const hunks: Hunk[] = [];
      let k = 0;
      while (k < rawOps.length) {
        while (k < rawOps.length && rawOps[k].kind === ' ') k++;
        if (k >= rawOps.length) break;
        const start = Math.max(0, k - CONTEXT);
        let oldStart = 0, newStart = 0;
        {
          let ol = 0, nl = 0;
          for (let q = 0; q < start; q++) {
            if (rawOps[q].kind !== '+') ol++;
            if (rawOps[q].kind !== '-') nl++;
          }
          oldStart = ol + 1;
          newStart = nl + 1;
        }
        const hunkOps: Array<{ kind: ' ' | '-' | '+'; text: string }> = [];
        for (let q = start; q < k; q++) hunkOps.push(rawOps[q]);
        let nonChangeStreak = 0;
        while (k < rawOps.length) {
          const op = rawOps[k];
          hunkOps.push(op);
          if (op.kind === ' ') {
            nonChangeStreak++;
            if (nonChangeStreak > 2 * CONTEXT) {
              hunkOps.splice(hunkOps.length - CONTEXT, CONTEXT);
              k++;
              break;
            }
          } else {
            nonChangeStreak = 0;
          }
          k++;
        }
        let oldLines = 0, newLines = 0;
        for (const op of hunkOps) {
          if (op.kind !== '+') oldLines++;
          if (op.kind !== '-') newLines++;
        }
        hunks.push({ oldStart, oldLines, newStart, newLines, ops: hunkOps });
      }

      const diff = formatUnified(hunks, absPath, args.path);
      const summaries: DiffHunkSummary[] = hunks.map(h => ({
        oldStart: h.oldStart,
        oldLines: h.oldLines,
        newStart: h.newStart,
        newLines: h.newLines,
        added: h.ops.filter(o => o.kind === '+').length,
        removed: h.ops.filter(o => o.kind === '-').length,
      }));
      const added = summaries.reduce((s, h) => s + h.added, 0);
      const removed = summaries.reduce((s, h) => s + h.removed, 0);
      return typedOk({
        path: absPath,
        diff,
        hunks: summaries,
        added,
        removed,
        unchanged: added === 0 && removed === 0,
      });
    } catch (err) {
      return typedErr(err instanceof Error ? err.message : String(err));
    }
  },
};

// ────────────────────────────────────────────────────────────────────────────
// apply_diff tool
// ────────────────────────────────────────────────────────────────────────────

const ApplyDiffArgsSchema = z.object({
  path: z.string().min(1),
  diff: z.string().min(1),
  fuzzyMatch: z.boolean().default(false),
  dryRun: z.boolean().default(false),
});

export { ApplyDiffArgsSchema };

type ApplyDiffArgs = z.infer<typeof ApplyDiffArgsSchema>;

interface ApplyDiffResult {
  path: string;
  applied: boolean;
  hunksApplied: number;
  hunksSkipped: number;
  finalContent: string;
  dryRun: boolean;
  reason?: string;
}

export const applyDiffTool: ToolDefinition<ApplyDiffArgs, ApplyDiffResult> = {
  name: 'apply_diff',
  description:
    'Apply a unified diff patch to a file. Parses ---/+++/@@ headers and applies each ' +
    'hunk at its DECLARED @@ position on the original file — zero relocation (ADR-0033): ' +
    'a drifted hunk is rejected with a structured WriteReject (meta.reject, status ' +
    'hunk_mismatch + minimalDiff), never searched for elsewhere. ' +
    'CRLF vs LF is ignored; the file keeps its original line endings. With fuzzyMatch=true, ' +
    'whitespace differences are tolerated — still only at the declared position. ' +
    'With dryRun=true, returns the final content without writing. ' +
    'Atomic: if any hunk fails, no partial write occurs.',
  permissions: ['write'],
  sideEffect: 'local',
  timeoutMs: 15000,
  inputSchema: ApplyDiffArgsSchema,
  execute: async (args, ctx) => {
    try {
      const absPath = path.isAbsolute(args.path) ? args.path : path.join(ctx.cwd, args.path);
      let current = '';
      try {
        current = await fs.readFile(absPath, 'utf-8');
      } catch (err) {
        if ((err as NodeJS.ErrnoException).code !== 'ENOENT') {
          return typedErr(err instanceof Error ? err.message : String(err));
        }
        // ENOENT → empty file
      }
      let parsed: ParsedDiff;
      try {
        parsed = parseUnified(args.diff);
      } catch (err) {
        return typedErr(`Parse failed: ${err instanceof Error ? err.message : String(err)}`);
      }
      if (parsed.hunks.length === 0) {
        return typedErr('No hunks found in diff');
      }
      // Apply ALL hunks atomically against the ORIGINAL file at their DECLARED
      // positions. Each hunk's oldStart refers to the original file, not the
      // post-previous-hunk state. No hunk is ever relocated (ADR-0033).
      const nl = detectNewline(current);
      const originalLines = splitLinesLF(current);
      const result = applyAllHunks(originalLines, parsed.hunks, args.fuzzyMatch);
      const finalContent = fromLF(result.lines.join('\n'), nl);
      const ok = result.hunksSkipped === 0;
      if (ok && !args.dryRun) {
        await fs.mkdir(path.dirname(absPath), { recursive: true });
        await fs.writeFile(absPath, finalContent, 'utf-8');
      }
      if (!ok) {
        // ADR-0033: structured reject on the failed hunk. Atomic — nothing was
        // written. The value keeps applied:false + counters for compatibility;
        // the machine payload (status/minimalDiff/span/next) rides in meta.reject.
        const failed = result.failedHunk;
        const startLine = failed ? Math.max(0, failed.oldStart - 1) : 0;
        const reject: WriteReject = {
          ok: false,
          status: 'hunk_mismatch',
          path: absPath,
          ...(failed ? { span: { startLine, endLine: startLine + failed.oldLines } } : {}),
          minimalDiff: failed ? formatUnified([failed], absPath, args.path) : '',
          next: { action: 're-read', path: absPath },
        };
        return typedOk(
          {
            path: absPath,
            applied: false,
            hunksApplied: result.hunksApplied,
            hunksSkipped: result.hunksSkipped,
            finalContent: '', // omit content on reject to save tokens
            dryRun: args.dryRun,
            reason: result.lastReason,
          },
          { status: 'failed', reject },
        );
      }
      return typedOk({
        path: absPath,
        applied: true,
        hunksApplied: result.hunksApplied,
        hunksSkipped: result.hunksSkipped,
        finalContent: args.dryRun ? '' : finalContent, // omit content on dryRun to save tokens
        dryRun: args.dryRun,
        reason: undefined,
      });
    } catch (err) {
      return typedErr(err instanceof Error ? err.message : String(err));
    }
  },
};